import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  FileText, Download, Calendar, Plus, CheckCircle2, XCircle, Loader2, Trash2,
  Briefcase, User,
} from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { LEAVE_STATUS, LEAVE_TYPES } from '../lib/leaveLabels';
import { AbsenceCalendarTab } from '../components/staff/AbsenceCalendarTab';

const STATUS_STYLE = LEAVE_STATUS;
const TYPE_LABEL = LEAVE_TYPES;

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('fr-FR') : '—');
const fmtDT = (iso) => (iso ? new Date(iso).toLocaleString('fr-FR') : '—');

const MyHR = () => {
  const { user } = useAuth();
  const [payslips, setPayslips] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [pendingForMe, setPendingForMe] = useState([]);
  const [balance, setBalance] = useState(null);
  const [team, setTeam] = useState([]);
  useEffect(() => {
    if (['admin', 'super_admin'].includes(user?.role)) return;
    api.get('/leave-requests/team-members').then((r) => setTeam(r.data || [])).catch(() => {});
  }, [user?.role]);
  const [loading, setLoading] = useState(true);
  const [showNewRequest, setShowNewRequest] = useState(false);
  const [decisionCtx, setDecisionCtx] = useState(null); // { request, decision: 'approved'|'rejected' }
  const [decisionNote, setDecisionNote] = useState('');
  const [form, setForm] = useState({ request_type: 'leave', start_date: '', end_date: '', reason: '' });

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [ps, mine, toApprove, bal] = await Promise.all([
        api.get('/me/payslips'),
        api.get('/leave-requests/mine'),
        api.get('/leave-requests/to-approve').catch(() => ({ data: [] })),
        api.get('/me/leave-balance').catch(() => ({ data: null })),
      ]);
      setPayslips(ps.data || []);
      setMyRequests(mine.data || []);
      setPendingForMe(toApprove.data || []);
      setBalance(bal.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const isManager = pendingForMe.length > 0 || myRequests.some((r) => r.manager_id === user?.id);

  const downloadPayslip = async (payslip) => {
    try {
      const r = await api.get(`/payslips/${payslip.id}/pdf`, { responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `bulletin_${payslip.period || 'paye'}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => window.URL.revokeObjectURL(link.href), 1000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const submitRequest = async () => {
    if (!form.start_date) return toast.error('Date de début requise');
    if (form.request_type === 'leave' && !form.end_date) return toast.error('Date de fin requise pour un congé');
    if (form.request_type === 'other' && !form.reason.trim()) return toast.error("Précisez l'objet de votre demande");
    try {
      await api.post('/leave-requests', {
        request_type: form.request_type,
        start_date: form.start_date,
        end_date: form.request_type === 'advance_day' ? null : (form.end_date || null),
        reason: form.reason,
      });
      toast.success('Demande envoyée à votre manager');
      setShowNewRequest(false);
      setForm({ request_type: 'leave', start_date: '', end_date: '', reason: '' });
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const cancelMine = async (req) => {
    if (!window.confirm(`Annuler la demande du ${fmtDate(req.start_date)} ?`)) return;
    try {
      await api.delete(`/leave-requests/${req.id}`);
      toast.success('Demande annulée');
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const openDecision = (req, decision) => {
    setDecisionCtx({ req, decision });
    setDecisionNote('');
  };

  const submitDecision = async () => {
    if (!decisionCtx) return;
    try {
      await api.post(`/leave-requests/${decisionCtx.req.id}/${decisionCtx.decision === 'approved' ? 'approve' : 'reject'}`, {
        manager_note: decisionNote,
      });
      toast.success(decisionCtx.decision === 'approved' ? 'Demande approuvée' : 'Demande refusée');
      setDecisionCtx(null);
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const stats = useMemo(() => ({
    payslips: payslips.length,
    pending: myRequests.filter((r) => r.status === 'pending').length,
    approved: myRequests.filter((r) => r.status === 'approved').length,
    to_approve: pendingForMe.length,
  }), [payslips, myRequests, pendingForMe]);

  return (
    <div className="space-y-6 pb-10" data-testid="my-hr-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#2B3A2C] flex items-center gap-2">
            <Briefcase className="w-7 h-7 text-[#B85C38]" /> Mon Espace RH
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Bienvenue <b>{user?.name}</b>. Consultez vos bulletins de paie et gérez vos demandes de congés.
          </p>
        </div>
        <Button onClick={() => setShowNewRequest(true)} className="bg-[#B85C38] hover:bg-[#8F4426] text-white" data-testid="new-leave-btn">
          <Plus className="w-4 h-4 mr-2" /> Nouvelle demande
        </Button>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardContent className="p-4"><div className="text-xs uppercase font-bold text-gray-500">Fiches de paie</div><div className="text-2xl font-bold text-[#2B3A2C] mt-1">{stats.payslips}</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="text-xs uppercase font-bold text-gray-500">En attente</div><div className="text-2xl font-bold text-amber-700 mt-1">{stats.pending}</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="text-xs uppercase font-bold text-gray-500">Approuvées</div><div className="text-2xl font-bold text-emerald-700 mt-1">{stats.approved}</div></CardContent></Card>
        {isManager && <Card className="border-[#B85C38]/40"><CardContent className="p-4"><div className="text-xs uppercase font-bold text-[#B85C38]">À valider</div><div className="text-2xl font-bold text-[#B85C38] mt-1">{stats.to_approve}</div></CardContent></Card>}
      </div>

      {/* Solde de congés */}
      {balance && (
        <Card className="border-l-4 border-l-[#B85C38] bg-gradient-to-r from-[#F5EFE1]/60 to-white" data-testid="leave-balance-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="text-xs uppercase font-bold text-[#8F4426] flex items-center gap-1">
                  <Calendar className="w-3.5 h-3.5" /> Solde de congés {balance.year}
                </div>
                <div className="text-3xl font-bold text-[#2B3A2C] mt-1">
                  {balance.balance.toFixed(1)} <span className="text-base font-normal text-gray-500">jour(s) disponibles</span>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-[11px] uppercase text-gray-500 font-bold">Pris cette année</div>
                  <div className="text-xl font-bold text-emerald-700 mt-0.5">{balance.taken_this_year.toFixed(1)}</div>
                </div>
                <div>
                  <div className="text-[11px] uppercase text-gray-500 font-bold">En attente</div>
                  <div className="text-xl font-bold text-amber-700 mt-0.5">{balance.pending_requested.toFixed(1)}</div>
                </div>
                <div>
                  <div className="text-[11px] uppercase text-gray-500 font-bold">Après approbation</div>
                  <div className={`text-xl font-bold mt-0.5 ${balance.projected_remaining < 0 ? 'text-red-600' : 'text-[#2B3A2C]'}`}>
                    {balance.projected_remaining.toFixed(1)}
                  </div>
                </div>
              </div>
            </div>
            {balance.pending_requested > balance.balance && (
              <div className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1">
                ⚠ Vos demandes en attente dépassent votre solde disponible ({balance.pending_requested.toFixed(1)} demandés / {balance.balance.toFixed(1)} disponibles).
              </div>
            )}
            {balance.accrual_per_month > 0 && (
              <div className="mt-2 text-[11px] text-gray-500 italic">
                Crédit automatique : <b>+{balance.accrual_per_month.toFixed(1)} jour(s)</b> ajoutés à votre solde chaque mois.
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="payslips" className="space-y-4">
        <TabsList className="bg-[#F5EFE1]">
          <TabsTrigger value="payslips" data-testid="tab-payslips"><FileText className="w-4 h-4 mr-1" /> Mes bulletins</TabsTrigger>
          <TabsTrigger value="mine" data-testid="tab-mine"><Calendar className="w-4 h-4 mr-1" /> Mes demandes</TabsTrigger>
          {isManager && <TabsTrigger value="approve" data-testid="tab-approve"><User className="w-4 h-4 mr-1" /> À approuver ({stats.to_approve})</TabsTrigger>}
          {team.length > 0 && <TabsTrigger value="team-calendar" data-testid="tab-team-calendar"><Calendar className="w-4 h-4 mr-1" /> Calendrier de l'équipe</TabsTrigger>}
        </TabsList>

        {team.length > 0 && <TabsContent value="team-calendar"><AbsenceCalendarTab /></TabsContent>}

        {/* Bulletins de paie */}
        <TabsContent value="payslips">
          <Card>
            <CardHeader>
              <CardTitle>Bulletins de paie</CardTitle>
              <CardDescription>Historique complet — cliquez sur « Télécharger » pour obtenir le PDF.</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-[#B85C38]" /></div> :
                payslips.length === 0 ? <p className="text-sm text-gray-500 text-center py-6">Aucun bulletin de paie disponible pour le moment.</p> :
                (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="text-left border-b border-[#B85C38]/20 text-[#8F4426] uppercase text-xs">
                        <tr><th className="p-2">Période</th><th className="p-2">Net à payer</th><th className="p-2">Statut</th><th className="p-2 text-right">Action</th></tr>
                      </thead>
                      <tbody>
                        {payslips.map((p) => (
                          <tr key={p.id} className="border-b hover:bg-[#F5EFE1]/40">
                            <td className="p-2 font-mono text-[#2B3A2C]">{p.period}</td>
                            <td className="p-2 font-semibold">{(p.net_pay || 0).toLocaleString('fr-FR')} FCFA</td>
                            <td className="p-2">
                              {p.paid ? <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">Payé</Badge>
                                       : <Badge className="bg-amber-100 text-amber-800 border-amber-200">En attente</Badge>}
                            </td>
                            <td className="p-2 text-right">
                              <Button size="sm" variant="outline" onClick={() => downloadPayslip(p)} data-testid={`download-payslip-${p.id}`}>
                                <Download className="w-3.5 h-3.5 mr-1" /> Télécharger
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Mes demandes */}
        <TabsContent value="mine">
          <Card>
            <CardHeader>
              <CardTitle>Mes demandes</CardTitle>
              <CardDescription>Suivi de vos demandes de congés, absences et autres.</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-[#B85C38]" /></div> :
                myRequests.length === 0 ? <p className="text-sm text-gray-500 text-center py-6">Aucune demande. Cliquez sur « Nouvelle demande » pour commencer.</p> :
                (
                  <div className="space-y-2">
                    {myRequests.map((r) => {
                      const S = STATUS_STYLE[r.status] || STATUS_STYLE.pending;
                      const Icon = S.icon;
                      return (
                        <div key={r.id} className="border border-[#B85C38]/20 rounded-lg p-3 hover:bg-[#F5EFE1]/30" data-testid={`request-${r.id}`}>
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <b className="text-[#2B3A2C]">{TYPE_LABEL[r.request_type] || r.request_type}</b>
                                <Badge className={`border ${S.className} flex items-center gap-1`}><Icon className="w-3 h-3" />{S.label}</Badge>
                              </div>
                              <div className="text-sm text-gray-700 mt-1">
                                Du <b>{fmtDate(r.start_date)}</b>{r.end_date && r.end_date !== r.start_date && <> au <b>{fmtDate(r.end_date)}</b></>} · <span className="text-xs text-gray-500">{r.days_count} jour(s)</span>
                              </div>
                              {r.reason && <div className="text-xs text-gray-600 mt-1 italic">« {r.reason} »</div>}
                              {r.manager_note && <div className="text-xs mt-1"><b>Note manager :</b> <span className="text-gray-700">{r.manager_note}</span></div>}
                              <div className="text-[11px] text-gray-500 mt-1">Créée le {fmtDT(r.created_at)}{r.decided_by_name && <> · Décidée par {r.decided_by_name} le {fmtDT(r.decided_at)}</>}</div>
                            </div>
                            {r.status === 'pending' && (
                              <Button size="sm" variant="ghost" onClick={() => cancelMine(r)} className="text-red-600 hover:bg-red-50" data-testid={`cancel-${r.id}`}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* À approuver */}
        {isManager && (
          <TabsContent value="approve">
            <Card>
              <CardHeader>
                <CardTitle>Demandes à valider</CardTitle>
                <CardDescription>Les collaborateurs vous ont désigné comme manager. Approuvez ou refusez leurs demandes.</CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-[#B85C38]" /></div> :
                  pendingForMe.length === 0 ? <p className="text-sm text-gray-500 text-center py-6">Aucune demande en attente.</p> :
                  (
                    <div className="space-y-2">
                      {pendingForMe.map((r) => (
                        <div key={r.id} className="border-l-4 border-l-[#B85C38] bg-[#F5EFE1]/30 rounded-lg p-3" data-testid={`pending-${r.id}`}>
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <User className="w-4 h-4 text-[#B85C38]" />
                                <b className="text-[#2B3A2C]">{r.user_name}</b>
                                <Badge variant="outline">{TYPE_LABEL[r.request_type]}</Badge>
                              </div>
                              <div className="text-sm text-gray-700 mt-1">
                                Du <b>{fmtDate(r.start_date)}</b>{r.end_date && r.end_date !== r.start_date && <> au <b>{fmtDate(r.end_date)}</b></>} · <span className="text-xs">{r.days_count} jour(s)</span>
                              </div>
                              {r.reason && <div className="text-xs text-gray-600 mt-1 italic">« {r.reason} »</div>}
                            </div>
                            <div className="flex gap-2">
                              <Button size="sm" onClick={() => openDecision(r, 'approved')} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`approve-${r.id}`}>
                                <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approuver
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => openDecision(r, 'rejected')} className="border-rose-400 text-rose-700 hover:bg-rose-50" data-testid={`reject-${r.id}`}>
                                <XCircle className="w-3.5 h-3.5 mr-1" /> Refuser
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* New request Dialog */}
      <Dialog open={showNewRequest} onOpenChange={(o) => !o && setShowNewRequest(false)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#2B3A2C]">Nouvelle demande</DialogTitle>
            <DialogDescription>
              Votre demande sera envoyée à votre manager pour validation.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs font-semibold">Type de demande *</Label>
              <Select value={form.request_type} onValueChange={(v) => setForm({ ...form, request_type: v })}>
                <SelectTrigger data-testid="request-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="leave">Congés (plusieurs jours)</SelectItem>
                  <SelectItem value="advance_day">Journée en avance (1 jour)</SelectItem>
                  <SelectItem value="absence">Absence (maladie, imprévu...)</SelectItem>
                  <SelectItem value="other">Autre demande</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs font-semibold">Date de début *</Label>
                <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="request-start-date" />
              </div>
              {form.request_type !== 'advance_day' && (
                <div>
                  <Label className="text-xs font-semibold">Date de fin {form.request_type === 'leave' ? '*' : '(optionnelle)'}</Label>
                  <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="request-end-date" />
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs font-semibold">{form.request_type === 'other' ? 'Objet de la demande *' : 'Motif (optionnel)'}</Label>
              <Textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Vacances familiales, rendez-vous médical..." rows={3} data-testid="request-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewRequest(false)}>Annuler</Button>
            <Button onClick={submitRequest} className="bg-[#B85C38] hover:bg-[#8F4426] text-white" data-testid="submit-request-btn">
              <Plus className="w-4 h-4 mr-2" /> Envoyer la demande
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Decision Dialog */}
      <Dialog open={!!decisionCtx} onOpenChange={(o) => !o && setDecisionCtx(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className={decisionCtx?.decision === 'approved' ? 'text-emerald-700' : 'text-rose-700'}>
              {decisionCtx?.decision === 'approved' ? 'Approuver la demande' : 'Refuser la demande'}
            </DialogTitle>
            <DialogDescription>
              {decisionCtx && <><b>{decisionCtx.req.user_name}</b> — {TYPE_LABEL[decisionCtx.req.request_type]} du {fmtDate(decisionCtx.req.start_date)}</>}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label className="text-xs font-semibold">Note (optionnelle)</Label>
            <Textarea value={decisionNote} onChange={(e) => setDecisionNote(e.target.value)} placeholder="Message à transmettre au collaborateur..." rows={3} data-testid="decision-note" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDecisionCtx(null)}>Annuler</Button>
            <Button
              onClick={submitDecision}
              className={decisionCtx?.decision === 'approved' ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-rose-600 hover:bg-rose-700 text-white'}
              data-testid="confirm-decision-btn"
            >
              Confirmer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyHR;
